import React from "react";
import "./CommercializationServices.css";
import PageHeader from "../../components/headers/PageHeader";
import wave from "../../assets/Images/waves/wave_mou.png";

export default function CommercializationServices() {
    return (
        <div className="metaanalysis-page">
            <PageHeader
                mediaSrc={wave}
                mediaType="image"
                subtitle="Evidence Strategy, RWE, HEOR & Market Access"
                title="Commercialization Services"
            />

            <section className="metaanalysis-section metaanalysis-introSection">
                <h2>
                    Transforming Clinical and Real-World Evidence into Regulatory,
                    Reimbursement, and Commercial Success
                </h2>

                <p>
                    Generating clinical evidence is only part of the journey. To achieve
                    successful commercialization, healthcare products must demonstrate
                    value not only to regulators, but also to payers, physicians,
                    healthcare systems, and patients.
                </p>

                <p>
                    Our multidisciplinary team combines biostatistics, epidemiology,
                    medical writing, health economics, regulatory science, real-world
                    evidence analytics, and market access expertise to help sponsors
                    generate, communicate, and maximize the value of their clinical
                    evidence.
                </p>

                <p>
                    From development strategy through reimbursement and lifecycle
                    management, we help organizations convert scientific data into
                    meaningful healthcare and commercial outcomes.
                </p>
            </section>

            <section className="metaanalysis-section metaanalysis-strategySection">
                <h2>Evidence Generation Strategy</h2>

                <p className="metaanalysis-sectionIntro">
                    We support evidence generation throughout the product lifecycle.
                </p>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Clinical Evidence Generation</h3>

                        <ul className="metaanalysis-list">
                            <li>Clinical development evidence planning.</li>
                            <li>Endpoint strategy development.</li>
                            <li>Statistical analysis planning.</li>
                            <li>Integrated Summary of Safety.</li>
                            <li>Integrated Summary of Efficacy.</li>
                            <li>Benefit-risk assessments.</li>
                            <li>Regulatory evidence packages.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Real-World Evidence</h3>
                        <span className="metaanalysis-cardSubtitle">RWE</span>

                        <ul className="metaanalysis-list">
                            <li>Real-world data strategy.</li>
                            <li>Retrospective database studies.</li>
                            <li>Registry analyses.</li>
                            <li>Claims database analyses.</li>
                            <li>Electronic health record studies.</li>
                            <li>Post-marketing evidence generation.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Evidence Gap Assessment</h3>

                        <ul className="metaanalysis-list">
                            <li>Regulatory approval.</li>
                            <li>Payer acceptance.</li>
                            <li>Market access.</li>
                            <li>Clinical adoption.</li>
                            <li>Competitive positioning.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="metaanalysis-section metaanalysis-synthesisSection">
                <h2>Evidence Synthesis and Advanced Analytics</h2>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Meta-Analysis</h3>

                        <ul className="metaanalysis-list">
                            <li>Fixed-effect meta-analysis.</li>
                            <li>Random-effects meta-analysis.</li>
                            <li>Bayesian meta-analysis.</li>
                            <li>Safety meta-analysis.</li>
                            <li>Efficacy meta-analysis.</li>
                            <li>Network Meta-Analysis.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Individual Patient Data Meta-Analysis</h3>
                        <span className="metaanalysis-cardSubtitle">
                            IPD Meta-Analysis
                        </span>

                        <ul className="metaanalysis-list">
                            <li>Patient-level evidence integration.</li>
                            <li>Advanced subgroup analyses.</li>
                            <li>Covariate-adjusted treatment comparisons.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="metaanalysis-section metaanalysis-heorSection">
                <h2>Health Economics and Outcomes Research</h2>

                <p className="metaanalysis-sectionIntro">
                    We help sponsors demonstrate clinical and economic value to
                    healthcare decision-makers.
                </p>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Economic Evaluations</h3>

                        <ul className="metaanalysis-list">
                            <li>Cost-effectiveness analyses.</li>
                            <li>Cost-utility analyses.</li>
                            <li>Budget impact models.</li>
                            <li>Cost-benefit assessments.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Outcomes Research</h3>

                        <ul className="metaanalysis-list">
                            <li>Patient-reported outcome analyses.</li>
                            <li>Quality-of-life assessments.</li>
                            <li>Treatment burden evaluations.</li>
                            <li>Healthcare resource utilization studies.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Value Demonstration</h3>

                        <ul className="metaanalysis-list">
                            <li>Comparative effectiveness research.</li>
                            <li>Economic evidence generation.</li>
                            <li>Value communication support.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="metaanalysis-section metaanalysis-accessSection">
                <h2>Market Access Strategy</h2>

                <p className="metaanalysis-sectionIntro">
                    Successful product launches require more than regulatory approval.
                    We support market access planning through:
                </p>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Access and Reimbursement Strategy</h3>

                        <ul className="metaanalysis-list">
                            <li>Market access assessments.</li>
                            <li>Payer evidence strategy.</li>
                            <li>Reimbursement planning.</li>
                            <li>Coverage strategy development.</li>
                            <li>Pricing support.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Evidence for Payers</h3>

                        <ul className="metaanalysis-list">
                            <li>Comparative effectiveness analyses.</li>
                            <li>Real-world evidence packages.</li>
                            <li>Value dossiers.</li>
                            <li>Payer evidence communication materials.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Global Market Access Support</h3>

                        <ul className="metaanalysis-list">
                            <li>US market access considerations.</li>
                            <li>European HTA support.</li>
                            <li>International reimbursement evidence planning.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="metaanalysis-section metaanalysis-commsSection">
                <h2>Medical Affairs and Scientific Communications</h2>

                <p className="metaanalysis-sectionIntro">
                    We help sponsors communicate evidence effectively to scientific,
                    clinical, and commercial audiences.
                </p>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Scientific Publications</h3>

                        <ul className="metaanalysis-list">
                            <li>Publication strategy development.</li>
                            <li>Manuscript preparation.</li>
                            <li>Abstract development.</li>
                            <li>Poster preparation.</li>
                            <li>Congress presentations.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Medical Communications</h3>

                        <ul className="metaanalysis-list">
                            <li>Scientific platform development.</li>
                            <li>Evidence communication materials.</li>
                            <li>Clinical summaries.</li>
                            <li>Medical education content.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="metaanalysis-section metaanalysis-commercialSection">
                <h2>Commercialization Support</h2>

                <p className="metaanalysis-sectionIntro">
                    Our commercialization services help organizations prepare for
                    successful product launch and lifecycle management.
                </p>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Launch Readiness</h3>

                        <ul className="metaanalysis-list">
                            <li>Evidence readiness assessments.</li>
                            <li>Competitive landscape analyses.</li>
                            <li>Clinical differentiation strategies.</li>
                            <li>Scientific communication planning.</li>
                        </ul>
                    </article>

                    <article className="metaanalysis-card">
                        <h3>Lifecycle Evidence Management</h3>

                        <ul className="metaanalysis-list">
                            <li>Post-approval evidence generation.</li>
                            <li>Label expansion support.</li>
                            <li>Real-world evidence programs.</li>
                            <li>Long-term safety and effectiveness evaluations.</li>
                        </ul>
                    </article>
                </div>
            </section>

            <section className="metaanalysis-section metaanalysis-patientSection">
                <h2>Patient-Centred Evidence Solutions</h2>

                <p className="metaanalysis-sectionIntro">
                    We help sponsors incorporate the patient perspective into development
                    and commercialization strategies.
                </p>

                <div className="metaanalysis-cardGrid">
                    <article className="metaanalysis-card">
                        <h3>Patient-Focused Services</h3>

                        <ul className="metaanalysis-list">
                            <li>Patient journey mapping.</li>
                            <li>Patient education programs.</li>
                            <li>Patient support strategy.</li>
                            <li>Patient engagement initiatives.</li>
                            <li>Patient-reported outcomes research.</li>
                        </ul>
                    </article>
                </div>

                <p className="metaanalysis-sectionOutro">
                    These activities help demonstrate product value beyond traditional
                    clinical endpoints.
                </p>
            </section>

            <section className="metaanalysis-section metaanalysis-rweSection">
                <h2>Real-World Evidence &amp; Observational Research</h2>

                <p className="metaanalysis-sectionIntro">
                    Eupraxia also undertakes RWE and observational studies in the US,
                    Middle East, India, and Singapore region.
                </p>

                <div className="metaanalysis-rweGrid">
                    <article className="metaanalysis-rweCard">
                        Medical Record Review Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Retrospective Chart Review Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Chart Abstraction Programs
                    </article>

                    <article className="metaanalysis-rweCard">
                        Electronic Health Record Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Patient Registry Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Claims Database Analyses
                    </article>

                    <article className="metaanalysis-rweCard">
                        Treatment Pattern Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Burden of Illness Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Outcomes Research Studies
                    </article>

                    <article className="metaanalysis-rweCard">
                        Comparative Effectiveness Research
                    </article>

                    <article className="metaanalysis-rweCard">
                        Post-Marketing Evidence Generation
                    </article>
                </div>
            </section>
        </div>
    );
}